import { notificationSeverityStyles } from '@web-app/components/Notifications/notification-severity-styles';
import { Button } from '@web-app/components/ui/Button/button';
import type { ActiveAnnouncement } from '@web-app/hooks/queries/announcements';
import { cn } from '@web-app/lib/utils';
import { format, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';
import { IconType } from 'react-icons';
import {
  LuCircleAlert,
  LuCircleCheck,
  LuTriangleAlert,
  LuX,
} from 'react-icons/lu';

type AnnouncementPriority = ActiveAnnouncement['priority'];

const priorityIcons: Record<AnnouncementPriority, IconType> = {
  info: LuCircleCheck,
  warning: LuTriangleAlert,
  critical: LuCircleAlert,
};

interface AnnouncementBannerProps {
  announcement: ActiveAnnouncement;
  onDismiss: (id: string) => void;
}

export const AnnouncementBanner = ({
  announcement,
  onDismiss,
}: AnnouncementBannerProps) => {
  const styles = notificationSeverityStyles[announcement.priority];
  const AnnouncementIcon = priorityIcons[announcement.priority];

  return (
    <div
      className={cn(
        'mb-4 w-full rounded-md border bg-white p-4 transition-shadow duration-200 ease-in-out hover:shadow-sm',
        styles.borderClassName,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-1 items-start gap-3">
          <AnnouncementIcon
            className={cn('mt-0.5 size-6 shrink-0', styles.iconClassName)}
          />
          <div>
            <p className="font-semibold text-foreground-legacy">
              {announcement.title}
            </p>
            {announcement.description && (
              <div
                className="mt-1 text-sm leading-relaxed text-foreground-legacy/70"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(announcement.description),
                }}
              />
            )}
            {announcement.publishedAt && (
              <p className="mt-2 text-xs text-muted-foreground-legacy">
                {format(parseISO(announcement.publishedAt), 'MMM d, yyyy')}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-muted-foreground-legacy hover:bg-muted-legacy hover:text-foreground-legacy"
          aria-label="Dismiss announcement"
          onClick={() => onDismiss(announcement.id)}
        >
          <LuX />
        </Button>
      </div>
    </div>
  );
};
